import type { DisplayPreference, MenuPreferences } from '@library/api-menu-experience'
import type { Recipe } from '@library/menu-service'

import { getCookingTimeLabel } from '../CookingTimeText'

export type RecipeSubtitleItem = {
  type: string
  text: string
  accessibleText: string
  iconType?: 'rating' | 'spice' | 'r2hTime'
}

type GetRecipeSubtitleItemsInput = {
  recipe: Recipe
  menuPreferences?: MenuPreferences
  numPortions: number
}

const DEFAULT_DISPLAY_PREFERENCES: DisplayPreference[] = [
  { type: 'prep-time', name: 'Cook time', selected: true },
  { type: 'calories', name: 'Calories', selected: true },
  { type: 'protein', name: 'Protein (g)', selected: true },
  { type: 'five-a-day', name: '5-a-day', selected: true },
  { type: 'ratings', name: 'Ratings', selected: true },
]

const gramsWithOneDecimalPlace = (valueMg: number) => `${(valueMg / 1000).toFixed(1)}g`
const gramsWithTwoDecimalPlaces = (valueMg: number) => `${(valueMg / 1000).toFixed(2)}g`

const getSelectedDisplayPreferences = ({
  displayPreferences,
  isReadyToHeat,
}: {
  displayPreferences?: DisplayPreference[]
  isReadyToHeat: boolean
}) => {
  if (!displayPreferences?.length) {
    return DEFAULT_DISPLAY_PREFERENCES
  }

  const selectedDisplayPreferences = displayPreferences.filter(({ selected }) => selected)

  if (
    isReadyToHeat &&
    !selectedDisplayPreferences.some((preference) => preference.type === 'prep-time')
  ) {
    return [{ type: 'prep-time', name: 'Cook time', selected: true }, ...selectedDisplayPreferences]
  }

  return selectedDisplayPreferences
}

const getSubtitleItem = ({
  preferenceId,
  recipe,
  numPortions,
}: {
  preferenceId: string
  recipe: Recipe
  numPortions: number
}): RecipeSubtitleItem | null => {
  switch (preferenceId) {
    case 'prep-time': {
      const cookingTimeLabel = getCookingTimeLabel({ recipe, numPortions })
      const isReadyToHeat = recipe.preparationType === 'heat-only'

      return cookingTimeLabel
        ? {
            type: 'prep-time',
            text: cookingTimeLabel,
            accessibleText: `Cooking time, ${recipe.prepTime} minutes${
              isReadyToHeat ? ` times ${numPortions}` : ''
            }`,
            ...(isReadyToHeat && { iconType: 'r2hTime' as const }),
          }
        : null
    }
    case 'calories': {
      const calories = recipe.nutritionalInformation.perPortion.energyKcal

      return calories > 0
        ? { type: 'calories', text: `${calories} kcal`, accessibleText: `${calories} calories` }
        : null
    }
    case 'ratings': {
      const rating = recipe.rating.average

      return rating > 0
        ? {
            type: 'ratings',
            text: rating.toFixed(1),
            accessibleText: `Rated ${rating.toFixed(1)} out of 5`,
            iconType: 'rating',
          }
        : null
    }
    case 'protein': {
      const proteinMg = recipe.nutritionalInformation.perPortion.proteinMg

      return proteinMg > 0
        ? {
            type: 'protein',
            text: `${gramsWithOneDecimalPlace(proteinMg)} protein`,
            accessibleText: `${gramsWithOneDecimalPlace(proteinMg)} protein`,
          }
        : null
    }
    case 'fat': {
      const fatMg = recipe.nutritionalInformation.perPortion.fatMg

      return fatMg > 0
        ? {
            type: 'fat',
            text: `${gramsWithOneDecimalPlace(fatMg)} fat`,
            accessibleText: `${gramsWithOneDecimalPlace(fatMg)} fat`,
          }
        : null
    }
    case 'fibre': {
      const fibreMg = recipe.nutritionalInformation.perPortion.fibreMg

      return fibreMg && fibreMg > 0
        ? {
            type: 'fibre',
            text: `${gramsWithOneDecimalPlace(fibreMg)} fibre`,
            accessibleText: `${gramsWithOneDecimalPlace(fibreMg)} fibre`,
          }
        : null
    }
    case 'salt': {
      const saltMg = recipe.nutritionalInformation.perPortion.saltMg

      return saltMg > 0
        ? {
            type: 'salt',
            text: `${gramsWithTwoDecimalPlaces(saltMg)} salt`,
            accessibleText: `${gramsWithTwoDecimalPlaces(saltMg)} salt`,
          }
        : null
    }
    case 'carbs': {
      const carbsMg = recipe.nutritionalInformation.perPortion.carbsMg

      return carbsMg > 0
        ? {
            type: 'carbs',
            text: `${gramsWithOneDecimalPlace(carbsMg)} carbs`,
            accessibleText: `${gramsWithOneDecimalPlace(carbsMg)} carbs`,
          }
        : null
    }
    case 'spice-level':
      return recipe.spiceLevel?.name
        ? {
            type: 'spice-level',
            text: recipe.spiceLevel.name,
            accessibleText: `Spice level: ${recipe.spiceLevel.name}`,
            iconType: 'spice',
          }
        : null
    case 'five-a-day':
      return recipe.fiveADay && recipe.fiveADay > 0
        ? {
            type: 'five-a-day',
            text: `${recipe.fiveADay} of 5 a day`,
            accessibleText: `${recipe.fiveADay} of 5 a day`,
          }
        : null
    case 'shelf-life':
      return recipe.shelfLife
        ? {
            type: 'shelf-life',
            text: `Use within ${recipe.shelfLife.minDays}-${recipe.shelfLife.maxDays} days`,
            accessibleText: `Use within ${recipe.shelfLife.minDays} to ${recipe.shelfLife.maxDays} days`,
          }
        : null
    default:
      return null
  }
}

export const getRecipeSubtitleItems = ({
  recipe,
  menuPreferences,
  numPortions,
}: GetRecipeSubtitleItemsInput): RecipeSubtitleItem[] =>
  getSelectedDisplayPreferences({
    displayPreferences: menuPreferences?.display?.list,
    isReadyToHeat: recipe.preparationType === 'heat-only',
  }).reduce<RecipeSubtitleItem[]>((subtitleItems, preference) => {
    const subtitleItem = getSubtitleItem({ preferenceId: preference.type, recipe, numPortions })

    return subtitleItem ? [...subtitleItems, subtitleItem] : subtitleItems
  }, [])
